import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../../services/api';
import toast from 'react-hot-toast';
import {
  PlayIcon,
  StopIcon,
  ClockIcon,
  CalendarDaysIcon
} from '@heroicons/react/24/outline';
import {
  VA_TIMEZONE_OPTIONS,
  DEFAULT_VA_TIMEZONE,
  MAX_SCHEDULE_MINUTES,
  shiftDurationMinutes,
  formatShiftDuration
} from '../../utils/vaTimezone';

function formatHours(minutes) {
  const h = Math.floor((minutes || 0) / 60);
  const m = (minutes || 0) % 60;
  if (h === 0) return `${m}m`;
  return `${h}h ${m}m`;
}

function dayLabel(dateKey) {
  const d = new Date(`${dateKey}T12:00:00`);
  return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}

function renderDayScheduleStatus(day) {
  if (day.scheduleStatus === 'missed') {
    return <span className="text-red-600 dark:text-red-400 font-medium">Missed shift</span>;
  }
  if (day.scheduleStatus === 'late') {
    return <span className="text-amber-600 dark:text-amber-400 font-medium">{day.lateLabel || 'Late'}</span>;
  }
  if (day.scheduledMinutes > 0) {
    return <span className="text-emerald-600 dark:text-emerald-400">OK</span>;
  }
  return <span className="text-gray-400">—</span>;
}

const VAWorkTimePanel = () => {
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [scheduleDraft, setScheduleDraft] = useState({});
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [scheduleTimezone, setScheduleTimezone] = useState(DEFAULT_VA_TIMEZONE);
  const [savingTimezone, setSavingTimezone] = useState(false);
  const [weekDays, setWeekDays] = useState([]);

  const fetchStatus = useCallback(async () => {
    const res = await api.get('/va/work-time/status');
    if (res.data.success) {
      setStatus(res.data.status);
      if (res.data.status?.scheduleTimezone) {
        setScheduleTimezone(res.data.status.scheduleTimezone);
      }
      if (res.data.status?.weekDays?.length) {
        setWeekDays(res.data.status.weekDays);
      }
    }
  }, []);

  const fetchHistory = useCallback(async () => {
    const res = await api.get('/va/work-time/history', { params: { period: 'weekly' } });
    if (res.data.success) setHistory(res.data.history);
  }, []);

  const loadScheduleDraft = useCallback(async (days) => {
    if (!days?.length) return;
    const schedRes = await api.get('/va/work-time/schedule', {
      params: { from: days[0], to: days[days.length - 1] }
    });
    if (schedRes.data.success) {
      const draft = {};
      days.forEach((date) => {
        draft[date] = { start: '', end: '' };
      });
      (schedRes.data.schedule || []).forEach((block) => {
        draft[block.date] = { start: block.start, end: block.end };
      });
      setScheduleDraft(draft);
      if (schedRes.data.scheduleTimezone) {
        setScheduleTimezone(schedRes.data.scheduleTimezone);
      }
    }
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([fetchStatus(), fetchHistory()]);
    } catch (err) {
      toast.error('Failed to load work time');
    } finally {
      setLoading(false);
    }
  }, [fetchStatus, fetchHistory]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  useEffect(() => {
    if (weekDays.length) loadScheduleDraft(weekDays);
  }, [weekDays, loadScheduleDraft]);

  useEffect(() => {
    if (!status?.activeSession) return undefined;
    const timer = window.setInterval(() => {
      fetchStatus();
    }, 30000);
    return () => window.clearInterval(timer);
  }, [status?.activeSession, fetchStatus]);

  const handleTimezoneChange = async (nextTimezone) => {
    setSavingTimezone(true);
    try {
      const res = await api.put('/va/work-time/timezone', { timezone: nextTimezone });
      if (res.data.success) {
        setScheduleTimezone(res.data.scheduleTimezone);
        toast.success('Timezone updated');
        await fetchStatus();
        if (weekDays.length) await loadScheduleDraft(weekDays);
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update timezone');
    } finally {
      setSavingTimezone(false);
    }
  };

  const handleStart = async () => {
    setActionLoading(true);
    try {
      await api.post('/va/work-time/start');
      toast.success('Work session started');
      await Promise.all([fetchStatus(), fetchHistory()]);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to start');
    } finally {
      setActionLoading(false);
    }
  };

  const handleFinish = async () => {
    setActionLoading(true);
    try {
      await api.post('/va/work-time/finish');
      toast.success('Work session finished');
      await Promise.all([fetchStatus(), fetchHistory()]);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to finish');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSaveSchedule = async () => {
    const blocks = weekDays
      .map((date) => ({
        date,
        start: scheduleDraft[date]?.start,
        end: scheduleDraft[date]?.end
      }))
      .filter((row) => row.start && row.end);

    if (!blocks.length && !weekDays.length) {
      toast.error('Add at least one scheduled shift');
      return;
    }

    for (const block of blocks) {
      const duration = shiftDurationMinutes(block.start, block.end);
      if (duration > MAX_SCHEDULE_MINUTES) {
        toast.error(`Each day can include at most 6 hours. ${dayLabel(block.date)} is ${formatShiftDuration(duration)}.`);
        return;
      }
    }

    setSavingSchedule(true);
    try {
      await api.put('/va/work-time/schedule', { blocks, replaceDates: weekDays });
      toast.success('Schedule saved');
      await fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save schedule');
    } finally {
      setSavingSchedule(false);
    }
  };

  const timezoneLabel = useMemo(
    () => VA_TIMEZONE_OPTIONS.find((opt) => opt.id === scheduleTimezone)?.label || scheduleTimezone,
    [scheduleTimezone]
  );

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6 text-center text-slate-500">
        Loading work tracker...
      </div>
    );
  }

  const active = status?.activeSession;
  const today = status?.today || {};

  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-sm font-medium text-indigo-600 dark:text-indigo-300">
              <ClockIcon className="w-5 h-5" />
              Work time tracker
            </div>
            <h2 className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">
              {active ? 'Currently working' : 'Ready to start'}
            </h2>
            <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
              Click Start when you begin work and Finish when you are done. Your hours are recorded automatically.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {!active ? (
              <button
                type="button"
                onClick={handleStart}
                disabled={actionLoading}
                className="inline-flex items-center gap-2 btn-primary disabled:opacity-50"
              >
                <PlayIcon className="w-5 h-5" />
                Start
              </button>
            ) : (
              <button
                type="button"
                onClick={handleFinish}
                disabled={actionLoading}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-red-600 hover:bg-red-700 text-white font-medium disabled:opacity-50"
              >
                <StopIcon className="w-5 h-5" />
                Finish
              </button>
            )}
          </div>
        </div>

        <div className="mt-6 grid sm:grid-cols-3 gap-4">
          <div className="rounded-xl bg-slate-50 dark:bg-gray-900/50 p-4 border border-slate-200 dark:border-gray-700">
            <div className="text-xs uppercase tracking-wide text-slate-500">Today</div>
            <div className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">
              {formatHours(today.workedMinutes || 0)}
            </div>
            {today.scheduledMinutes > 0 && (
              <div className="text-xs text-slate-500 mt-1">
                Scheduled {formatHours(today.scheduledMinutes)}
                {today.scheduleStatus === 'missed' && (
                  <span className="text-red-600 dark:text-red-400 ml-1">· missed shift</span>
                )}
                {today.scheduleStatus === 'late' && (
                  <span className="text-amber-600 dark:text-amber-400 ml-1">· {today.lateLabel || 'Late'}</span>
                )}
              </div>
            )}
          </div>
          <div className="rounded-xl bg-slate-50 dark:bg-gray-900/50 p-4 border border-slate-200 dark:border-gray-700">
            <div className="text-xs uppercase tracking-wide text-slate-500">This week</div>
            <div className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">
              {(status?.weekWorkedHours || 0).toFixed(1)}h
            </div>
          </div>
          <div className="rounded-xl bg-slate-50 dark:bg-gray-900/50 p-4 border border-slate-200 dark:border-gray-700">
            <div className="text-xs uppercase tracking-wide text-slate-500">Active session</div>
            <div className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">
              {active ? formatHours(active.elapsedMinutes || 0) : '—'}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <CalendarDaysIcon className="w-5 h-5 text-indigo-500" />
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Schedule (next 7 days)</h3>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-slate-500">Timezone</label>
            <select
              value={scheduleTimezone}
              disabled={savingTimezone}
              onChange={(e) => handleTimezoneChange(e.target.value)}
              className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-1.5 text-sm"
            >
              {VA_TIMEZONE_OPTIONS.map((opt) => (
                <option key={opt.id} value={opt.id}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>
        <p className="text-sm text-slate-500 mb-4">
          Set when you plan to work in <span className="font-medium text-slate-700 dark:text-slate-200">{timezoneLabel}</span>.
          You can schedule up to <span className="font-medium">6 hours per day</span>.
          If you schedule hours but do not click Start during that time, it will be flagged for your manager.
        </p>
        <div className="space-y-3">
          {weekDays.map((date) => {
            const start = scheduleDraft[date]?.start || '';
            const end = scheduleDraft[date]?.end || '';
            const duration = start && end ? shiftDurationMinutes(start, end) : 0;
            const tooLong = duration > MAX_SCHEDULE_MINUTES;
            return (
              <div key={date} className="grid sm:grid-cols-[180px_1fr_1fr_auto] gap-3 items-center">
                <div className="text-sm font-medium text-slate-700 dark:text-slate-200">{dayLabel(date)}</div>
                <input
                  type="time"
                  value={start}
                  onChange={(e) => setScheduleDraft((prev) => ({
                    ...prev,
                    [date]: { ...prev[date], start: e.target.value }
                  }))}
                  className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                />
                <input
                  type="time"
                  value={end}
                  onChange={(e) => setScheduleDraft((prev) => ({
                    ...prev,
                    [date]: { ...prev[date], end: e.target.value }
                  }))}
                  className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                />
                <div className={`text-xs min-w-[72px] ${tooLong ? 'text-red-600 dark:text-red-400 font-medium' : 'text-slate-500'}`}>
                  {duration > 0 ? formatShiftDuration(duration) : '—'}
                </div>
              </div>
            );
          })}
        </div>
        <button
          type="button"
          onClick={handleSaveSchedule}
          disabled={savingSchedule}
          className="btn-primary mt-4 disabled:opacity-50"
        >
          {savingSchedule ? 'Saving…' : 'Save schedule'}
        </button>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm overflow-x-auto">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">Your work history (this week)</h3>
        <table className="min-w-full text-sm">
          <thead className="text-xs uppercase text-slate-500">
            <tr>
              <th className="text-left py-2 pr-4">Date</th>
              <th className="text-left py-2 pr-4">Worked</th>
              <th className="text-left py-2 pr-4">Scheduled</th>
              <th className="text-left py-2">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {(history?.daily || []).map((day) => (
              <tr key={day.date}>
                <td className="py-2 pr-4">{dayLabel(day.date)}</td>
                <td className="py-2 pr-4 font-medium">{formatHours(day.workedMinutes || 0)}</td>
                <td className="py-2 pr-4">{day.scheduledMinutes ? formatHours(day.scheduledMinutes) : '—'}</td>
                <td className="py-2">
                  {renderDayScheduleStatus(day)}
                </td>
              </tr>
            ))}
            {(history?.daily || []).length === 0 && (
              <tr>
                <td colSpan={4} className="py-4 text-slate-500">No recorded hours yet this week.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default VAWorkTimePanel;
